import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import type { EmailTemplate } from '@/services/networks/emailTemplatesApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  type: 'type',
  objectState: 'objectState',
  team: 'team.name',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface EmailTemplateTableProps {
  emailTemplates: EmailTemplate[]
  loading?: boolean
  emptyMessage?: string
  selection?: EmailTemplate[]
  onSelectionChange?: (emailTemplates: EmailTemplate[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function EmailTemplateTable({
  emailTemplates,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: EmailTemplateTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<EmailTemplate, unknown>[] = [
    ...(selectable ? [selectionColumn<EmailTemplate>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      size: 120,
      cell: ({ row }) => <MutedCell value={t(`emailTemplates.types.${row.original.type}`)} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<EmailTemplate>({
      id: 'team',
      accessorFn: (row) => row.team?.name ?? '',
      header: t('emailTemplates.team'),
      size: 160,
      cell: ({ row }) => (
        <MutedCell value={row.original.team?.name || t('emailTemplates.noTeam')} />
      ),
    }),
    optionalColumn<EmailTemplate>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<EmailTemplate>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={emailTemplates}
      loading={loading}
      emptyMessage={emptyMessage ?? t('emailTemplates.noEmailTemplatesFound')}
      columnsKey="emailTemplates"
      rowHref={(row) => `/admin/email-templates/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
